import React from 'react';
import { PropTypes } from 'prop-types';
import { map, findLast } from 'lodash';
import styled, { css } from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { format } from '@origo_vl_banking/utils';

const TableDetailsContainer = styled.div`
  display: grid;
  grid-template-columns: fit-content(33%);
  grid-template-areas:
    'leftData rightData'
    'leftData action';
  word-break: break-word;

  ${({ flexDirection, rightDataLength, leftDataLength }) => {
    if (flexDirection === 'row') {
      if (rightDataLength !== leftDataLength) {
        return css`
          grid-template-columns: ${leftDataLength}fr ${rightDataLength}fr;
        `;
      } else if (rightDataLength === leftDataLength) {
        const contentSize = `${(
          (rightDataLength / (rightDataLength + leftDataLength)) *
          100
        ).toFixed(0)}%`;
        return css`
          grid-template-columns: fit-content(${contentSize});
        `;
      }
    }
  }}

  height: ${props => (props.transitionDisabled ? '100%' : '0')};
  visibility: ${props => (props.transitionDisabled ? 'visible' : 'hidden')};
  opacity: ${props => (props.transitionDisabled ? '100' : '0')};

  transition: 250ms;

  @media (max-width: 840px) {
    grid-template-areas:
      'leftData'
      'leftData'
      'rightData'
      'action';
    grid-template-columns: 1fr;
  }
`;

const Table = styled.div`
  display: table;
  table-layout: fixed;
  font-size: 14px;
  empty-cells: show;
  overflow: hidden;
  width: 100%;
  /*min-width: 370px;*/
`;

const TableBody = styled.div`
  display: table-row-group;
  height: 100%;
  padding: 15px;
  margin: 15px;
`;

const TableRow = styled.div`
  display: table-row;
  width: 100%;
`;

const TableCell = styled.div`
  display: table-cell;
  padding-left: 10px;
  padding-top: ${props => (props.first ? '15px' : '4px')};
  padding-bottom: ${props => (props.last ? '13px' : '4px')};
  empty-cells: show;
  font-weight: ${props => props.heading && 600};
`;

const ActionContainer = styled.div`
  grid-area: action;
  justify-self: end;
`;

const DataColumn = styled.div`
  display: flex;
  flex-direction: ${({ flexDirection }) => flexDirection || 'column'};
  grid-area: ${({ gridArea }) => gridArea};

  @media (max-width: 840px) {
    flex-direction: column;
  }
`;

const getTable = (data, category, name) => {
  const tableArray = [];

  if (category) {
    tableArray.push(<TableRow key={`row.${category}`}>
      <TableCell first left heading>
        <FormattedMessage id={`${name}.${category}`} />
      </TableCell>
    </TableRow>);
  }

  const dataArray = Object.keys(data);

  map(data, (cellInfo, cellName) => {
    const isValidWithKey = cellInfo && Object.keys(cellInfo).length > 1 && cellInfo.isKey === true;

    const lastRow = (dataArray.indexOf(cellName) + 1) === dataArray.length;

    if (isValidWithKey) {
      tableArray.push(
        // eslint-disable-line
        <TableRow key={`row.${cellName}`}>
          <TableCell left>
            <FormattedMessage id={`${name}.${cellName}`} />
          </TableCell>
          <TableCell last={lastRow}>
            <FormattedMessage id={cellInfo.value} />
          </TableCell>
        </TableRow>);
    } else if (cellInfo !== null && typeof cellInfo !== 'boolean') {
      tableArray.push(
        // eslint-disable-line
        <TableRow key={`row.${cellName}`}>
          <TableCell left>
            <FormattedMessage id={`${name}.${cellName}`} />
          </TableCell>
          <TableCell last={lastRow}>{format.value(cellInfo)}</TableCell>
        </TableRow>);
    }
  });

  let shouldRender = true;

  if (category && tableArray.length === 1) {
    shouldRender = false;
  }

  return shouldRender && tableArray;
};

const TableDetails = ({
  transitionDisabled, leftData, rightData, action, name, flexDirection,
}) => (
  <TableDetailsContainer
    flexDirection={flexDirection}
    transitionDisabled={transitionDisabled}
    rightDataLength={Object.keys(rightData).length}
    leftDataLength={Object.keys(leftData).length}
  >
    {leftData && (
      <DataColumn gridArea="leftData" flexDirection={flexDirection}>
        {map(leftData, (element, heading) => (
          <Table key={`left.${heading}`}>
            <TableBody>{getTable(element, heading, name)}</TableBody>
          </Table>
        ))}
      </DataColumn>
    )}
    {rightData && (
      <DataColumn gridArea="rightData" flexDirection={flexDirection}>
        {map(rightData, (element, heading) => (
          <Table key={`right.${heading}`}>
            <TableBody>{getTable(element, heading, name)}</TableBody>
          </Table>
        ))}
      </DataColumn>
    )}
    {action && <ActionContainer>{action}</ActionContainer>}
  </TableDetailsContainer>
);

TableDetails.propTypes = {
  leftData: PropTypes.object,
  rightData: PropTypes.object,
  action: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  name: PropTypes.string.isRequired,
  flexDirection: PropTypes.string,
  transitionDisabled: PropTypes.bool,
};

TableDetails.defaultProps = {
  action: [],
  transitionDisabled: true,
  flexDirection: 'column',
  leftData: {},
  rightData: {},
};

export default TableDetails;
